import React, { useState, useEffect } from 'react';
import { useT } from '../i18n';
import { setProjectShared, setProjectShareEditable } from '../firebase/workspaceService';
import { redeemShareCode } from '../firebase/shareCodeService';
import { SHARE_KINDS } from '../constants/share';
import ShareToChat, { SendIcon } from './ShareToChat';
import './ShareButton.css';

// "Share" button + modal. Flips the project's `shared` flag (so the Firestore
// rules let anyone read it) and hands the owner a copyable public link:
//   /share/:userId/:projectId
// Renders nothing without a cloud project + signed-in user (guests/local trials
// have nothing to share).
//
// Modes:
//   • uncontrolled (default) — renders its own trigger button, manages open state.
//   • controlled — pass `open` + `onClose` and `showTrigger={false}` to drive the
//     modal from elsewhere (e.g. a project card's "Share" menu item).
export default function ShareButton({ projectId, projectName = '', user, compact = false, open: controlledOpen, onClose, showTrigger = true }) {
  const { t } = useT();
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const [busy, setBusy]       = useState(false);
  const [link, setLink]       = useState('');
  const [error, setError]     = useState(false);
  const [copied, setCopied]   = useState(false);
  // Editable-share: the owner ticks "Editable?" and pastes an editor's code.
  const [editable, setEditable]   = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeBusy, setCodeBusy]   = useState(false);
  const [codeMsg, setCodeMsg]     = useState(null); // { ok, text }
  // The conversation picker, opened over this modal.
  const [toChat, setToChat]       = useState(false);

  const closeModal = () => { if (isControlled) onClose?.(); else setInternalOpen(false); };

  // Whenever the modal opens, enable public read and build the link. Runs for
  // both the built-in trigger and externally-controlled opens.
  useEffect(() => {
    if (!open || !projectId || !user?.uid) return;
    setError(false);
    setCopied(false);
    setLink(`${window.location.origin}/share/${user.uid}/${projectId}`);
    setEditable(false); setCodeInput(''); setCodeMsg(null); setToChat(false);
    setBusy(true);
    setProjectShared(projectId, true)
      .catch(err => { console.error('[Share] enabling public read failed:', err); setError(true); })
      .finally(() => setBusy(false));
  }, [open, projectId, user?.uid]);

  if (!projectId || !user?.uid) return null;

  function handleOpen() { setInternalOpen(true); }

  // Owner toggles "Editable?" — turning it off revokes the editable flag.
  async function handleToggleEditable(next) {
    setEditable(next);
    setCodeMsg(null);
    if (!next) {
      try { await setProjectShareEditable(projectId, false); } catch (err) { console.error(err); }
    }
  }

  // Owner submits an editor's code: redeem it (bind editor↔project) and mark
  // the project editable so the /share viewer checks edit access.
  async function handleRedeemCode() {
    const code = codeInput.trim();
    if (!code || codeBusy) return;
    setCodeBusy(true);
    setCodeMsg(null);
    try {
      const res = await redeemShareCode(code, projectId);
      if (res.ok) {
        await setProjectShareEditable(projectId, true);
        setCodeMsg({ ok: true, text: t('share_code_ok') });
      } else {
        const key = res.reason === 'used' ? 'share_code_used'
          : res.reason === 'expired' ? 'share_code_expired'
          : res.reason === 'error' ? 'share_code_error' : 'share_code_invalid';
        setCodeMsg({ ok: false, text: t(key) });
      }
    } catch (err) {
      console.error('[Share] redeem code failed:', err);
      setCodeMsg({ ok: false, text: t('share_code_invalid') });
    } finally {
      setCodeBusy(false);
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Fallback for older / insecure-context browsers
      const ta = document.createElement('textarea');
      ta.value = link;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <>
      {showTrigger && (
        <button
          className={`share-btn${compact ? ' share-btn--compact' : ''}`}
          onClick={handleOpen}
          title={t('share_title')}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <circle cx="11" cy="2.6" r="1.9" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="3" cy="7" r="1.9" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="11" cy="11.4" r="1.9" stroke="currentColor" strokeWidth="1.2" />
            <path d="M9.3 3.6L4.7 6M4.7 8L9.3 10.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          {!compact && <span>{t('share')}</span>}
        </button>
      )}

      {open && (
        <div className="share-overlay" onClick={() => closeModal()}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <button className="share-modal-close" onClick={() => closeModal()} aria-label="Close">×</button>
            <h3 className="share-modal-title">{t('share_title')}</h3>
            <p className="share-modal-desc">{t('share_desc')}</p>

            <label className="share-field-label">{t('share_link_label')}</label>
            <div className="share-field-row">
              <input className="share-link-input" value={link} readOnly onFocus={(e) => e.target.select()} />
              <button className="share-copy-btn" onClick={handleCopy}>
                {copied ? t('share_copied') : t('share_copy')}
              </button>
            </div>

            {busy && <p className="share-status">{t('share_enabling')}</p>}
            {error && <p className="share-status share-status--error">{t('share_error')}</p>}

            {/* SEND IT, rather than hand over a link to send elsewhere. The
                same public link, delivered as a card in a conversation — which
                is what people were doing with it by hand anyway.

                DISABLED WHILE `busy`: the effect above is still flipping the
                project to publicly readable, and a card that arrived before
                that landed would open a permission error for the recipient. */}
            <button
              type="button"
              className="share-chat-btn"
              onClick={() => setToChat(true)}
              disabled={busy || error}
            >
              <SendIcon />
              <span>{t('share_to_chat')}</span>
            </button>

            {/* Editable share — grant one editor (via their share code) the
                ability to edit this design through the link. */}
            <label className="share-editable-row">
              <input type="checkbox" checked={editable} onChange={e => handleToggleEditable(e.target.checked)} />
              <span>{t('share_editable_label')}</span>
            </label>

            {editable && (
              <div className="share-code-box">
                <p className="share-code-hint">{t('share_editable_hint')}</p>
                <div className="share-field-row">
                  <input
                    className="share-link-input"
                    placeholder={t('share_code_ph')}
                    value={codeInput}
                    maxLength={16}
                    onChange={e => { setCodeInput(e.target.value); setCodeMsg(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleRedeemCode(); }}
                  />
                  <button className="share-copy-btn" onClick={handleRedeemCode} disabled={codeBusy || !codeInput.trim()}>
                    {codeBusy ? '…' : t('share_code_apply')}
                  </button>
                </div>
                {codeMsg && (
                  <p className={`share-status${codeMsg.ok ? '' : ' share-status--error'}`}>{codeMsg.text}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Portalled to the body from inside, so it clears this modal's own
          stacking context and its close-on-backdrop-click. */}
      {toChat && (
        <ShareToChat
          share={{
            kind: SHARE_KINDS.PROJECT,
            ownerUid: user.uid,
            refId: projectId,
            title: projectName,
          }}
          onClose={() => setToChat(false)}
        />
      )}
    </>
  );
}
